import React from 'react';
import { IStorefrontProcurementVendorSettings } from '../../../models/Storefront';
import { useGetAllVendorSettingsQuery, useGetVendorsQuery } from '../../../store/Api';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootReducer';
import { useGetOrganizationQuery } from '../../../store/endpoints/OrganizationsEndpoints';
import { IVendorSettings } from '../../../models/Settings';
import { DefaultButton, Icon, IconButton, Panel, Stack } from '@fluentui/react';
import ToggleField from '../../Common/ToggleField';
import NumberField from '../../Inputs/NumberField';
import { useLocation } from 'react-router-dom';
import { useUrlPanelState } from '../../../hooks/useUrlState';
import { DynamicSetting } from '../../Common/Setting';

interface IFullfillmentVendorTableProps {
    settings?: {[key: string]: IStorefrontProcurementVendorSettings};
    onChange?: (updatedSettings: {[key: string]: IStorefrontProcurementVendorSettings}) => void;
    disabled?: boolean;
}

const FulfillmentVendorTable: React.FC<IFullfillmentVendorTableProps> = (props) => {
    const [defaultCatalog, setDefaultCatalog] = React.useState<string>();
    const selectedOrganization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const organization = useGetOrganizationQuery(selectedOrganization ?? '', {skip: !selectedOrganization || selectedOrganization === ''});
    const vendorSettings = useGetAllVendorSettingsQuery();
    const vendors = useGetVendorsQuery(defaultCatalog ?? "", {skip: !defaultCatalog})

    // In the URL (?panel=fulfillmentVendor&instance=<sourceVendor>) so documentation can link
    // straight to one vendor's fulfillment settings.
    const editPanel = useUrlPanelState('fulfillmentVendor');
    const editingVendor = editPanel.instanceId ?? null;
    const location = useLocation();

    React.useEffect(() => {
        if(organization.data) {
            setDefaultCatalog(organization.data.defaultCatalog);
        }
    },[organization.data]);

    /*
     * The area only knows to open this panel (?panel=fulfillmentVendor) - it has no picker to say
     * which vendor, since vendors are not one of the registered instance types. The vendor id
     * travels inside the setting id instead, the same way a focused banner selects its slide.
     */
    React.useEffect(() => {
        if (!editPanel.isOpen || editPanel.instanceId) return;
        const focus = new URLSearchParams(location.search).get('focus');
        const match = /^storefront\.fulfillmentVendor\.([^.]+)\./.exec(focus ?? '');
        if (match) editPanel.open(match[1]);
    }, [editPanel.isOpen, editPanel.instanceId, location.search]);

    return (
        <div>
            <h3>Vendor Settings</h3>
            {/* Table and logic will go here */}
            <table className='data-table'>
                <thead>
                    <tr>
                        <th>Vendor</th>
                        <th style={{width: '125px'}}>Order Creation</th>
                        <th style={{width: '125px'}}>Auto-Approval</th>
                        <th style={{width: '100px'}}>Dropship</th>
                        <th style={{width: '100px'}}>Backorder</th>
                        <th style={{width: '50px'}}>Edit</th>
                    </tr>
                </thead>
                <tbody>
                    {vendors.data?.items && vendors.data.items.map((vendor) =>
                        <FulfillmentVendorRow
                            key={vendor}
                            vendorKey={vendor}
                            displaySettings={vendorSettings.data?.items.find(v => v.sourceVendor === vendor)}
                            settings={props.settings ? props.settings[vendor] : undefined}
                            disabled={props.disabled}
                            onEdit={() => editPanel.open(vendor)}
                            />
                    )}
                </tbody>
            </table>
            {editingVendor && <FulfillmentVendorEditPanel
                vendorKey={editingVendor}
                settings={props.settings ? props.settings[editingVendor] : undefined}
                displaySettings={vendorSettings.data?.items.find(v => v.sourceVendor === editingVendor)}
                isOpen={true}
                onClose={() => editPanel.close()}
                onChange={(updatedSettings) => {
                    if (!props.onChange) return;
                    const newSettings = {...props.settings};
                    newSettings[editingVendor] = updatedSettings;
                    props.onChange(newSettings);
                }}
                />}
        </div>
    );
}

interface IFullfillmentVendorRowProps {
    vendorKey: string;
    settings?: IStorefrontProcurementVendorSettings;
    disabled?: boolean;
    onEdit: () => void;
    displaySettings?: IVendorSettings;
}

const FulfillmentVendorRow: React.FC<IFullfillmentVendorRowProps> = (props) => {
    const editPanelOpen = React.useState(false);

    const autoApproveDisplay = () => {
        if (!props.settings?.autoApproveOrders) return null;
        if (props.settings.autoApproveCostThreshold || props.settings.autoApproveItemCountThreshold) {
            return <span>
            {props.settings.autoApproveCostThreshold && props.settings.autoApproveCostThreshold > 0 &&
                <p style={{margin: 0}}>Cost &lt;= {props.settings.autoApproveCostThreshold}</p>}
            {props.settings.autoApproveItemCountThreshold && props.settings.autoApproveItemCountThreshold > 0 &&
                <p style={{margin: 0}}>Items &lt;= {props.settings.autoApproveItemCountThreshold}</p>}
            </span>
        }
        if (props.settings.autoApproveCostThreshold && props.settings.autoApproveCostThreshold > 0) {
            return <span>Cost &lt;= {props.settings.autoApproveCostThreshold}</span>;
        }
        if (props.settings.autoApproveItemCountThreshold && props.settings.autoApproveItemCountThreshold > 0) {
            return <span>Items &lt;= {props.settings.autoApproveItemCountThreshold}</span>;
        }
        return <span>All Orders</span>;
    }

    return (
        <tr>
            <td>{props.displaySettings?.vendorDisplayName ?? props.vendorKey}</td>
            <td style={{textAlign: 'center'}}>{props.settings?.enableOrderCreation ? <Icon iconName="CheckMark" /> : null}</td>
            <td style={{textAlign: 'center'}}>
                {autoApproveDisplay()}
            </td>
            <td style={{textAlign: 'center'}}>{props.settings?.dropShipToEndCustomer ? <Icon iconName="CheckMark" /> : null}</td>
            <td style={{textAlign: 'center'}}>{props.settings?.allowBackorder ? <Icon iconName="CheckMark" /> : null}</td>
            <td style={{textAlign: 'center'}}>
                <IconButton iconProps={{iconName: 'edit'}} onClick={props.onEdit} disabled={props.disabled} />
            </td>
        </tr>
    );
}

interface IFulfillmentVendorEditPanelProps {
    vendorKey: string;
    settings?: IStorefrontProcurementVendorSettings;
    displaySettings?: IVendorSettings;
    isOpen: boolean;
    onClose: () => void;
    onChange?: (updatedSettings: IStorefrontProcurementVendorSettings) => void;
}

const FulfillmentVendorEditPanel: React.FC<IFulfillmentVendorEditPanelProps> = (props) => {
    const [settings, setSettings] = React.useState<IStorefrontProcurementVendorSettings>(props.settings ?? {});

    React.useEffect(() => {
        setSettings(props.settings ?? {});
    }, [props.settings]);

    const save = () => {
        if (props.onChange) {
            props.onChange(settings);
        }
        props.onClose();
    }


    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onClose}
            closeButtonAriaLabel='Close'
            headerText={props.displaySettings?.vendorDisplayName ?? props.vendorKey}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{childrenGap: '1em'}}>
                    <DefaultButton primary onClick={save} style={{ flexGrow: 1}}>Save</DefaultButton>
                    <DefaultButton onClick={props.onClose} style={{ flexGrow: 1}}>Cancel</DefaultButton>
                </Stack>
            )}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                <DynamicSetting id={`storefront.fulfillmentVendor.${props.vendorKey}.enableOrderCreation`}>
                <ToggleField
                    label='Enable Order Creation'
                    inline
                    checked={settings.enableOrderCreation}
                    onChange={(e, c) => { setSettings((s) => ({...s, enableOrderCreation: !!c}));}}
                />
                </DynamicSetting>
                <DynamicSetting id={`storefront.fulfillmentVendor.${props.vendorKey}.autoApproveOrders`}>
                <ToggleField
                    label='Auto-Approve Orders'
                    inline
                    disabled={!settings.enableOrderCreation}
                    checked={settings.autoApproveOrders}
                    onChange={(e, c) => { setSettings((s) => ({...s, autoApproveOrders: !!c}));}}
                />
                </DynamicSetting>
                <DynamicSetting id={`storefront.fulfillmentVendor.${props.vendorKey}.autoApproveCostThreshold`}>
                <NumberField
                    label='Auto-Approve Cost Threshold'
                    disabled={!settings.enableOrderCreation || !settings.autoApproveOrders}
                    value={settings.autoApproveCostThreshold}
                    onChange={(v) => { setSettings((s) => ({...s, autoApproveCostThreshold: v}));}}
                />
                </DynamicSetting>
                <DynamicSetting id={`storefront.fulfillmentVendor.${props.vendorKey}.autoApproveItemCountThreshold`}>
                <NumberField
                    label='Auto-Approve Item Count Threshold'
                    disabled={!settings.enableOrderCreation || !settings.autoApproveOrders}
                    value={settings.autoApproveItemCountThreshold}
                    onChange={(v) => { setSettings((s) => ({...s, autoApproveItemCountThreshold: v}));}}
                />
                </DynamicSetting>
                <DynamicSetting id={`storefront.fulfillmentVendor.${props.vendorKey}.dropShipToEndCustomer`}>
                <ToggleField
                    label='Dropship to End Customer'
                    inline
                    disabled={!settings.enableOrderCreation}
                    checked={settings.dropShipToEndCustomer}
                    onChange={(e, c) => { setSettings((s) => ({...s, dropShipToEndCustomer: !!c}));}}
                />
                </DynamicSetting>
                <DynamicSetting id={`storefront.fulfillmentVendor.${props.vendorKey}.allowBackorder`}>
                <ToggleField
                    label='Allow Backorder'
                    inline
                    disabled={!settings.enableOrderCreation}
                    checked={settings.allowBackorder}
                    onChange={(e, c) => { setSettings((s) => ({...s, allowBackorder: !!c}));}}
                />
                </DynamicSetting>
            </Stack>
        </Panel>
    );
}


export default FulfillmentVendorTable;